import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import InfoIcon from './InfoIcon';
import { useValidation } from './ValidationContext';

const parseNumber = (val) => {
  if (typeof val !== 'string') return val;
  return val.replace(/\./g, '').replace(',', '.');
};

const validationRules = {
  required: (value) => {
    const stringValue = (value !== undefined) ? String(value) : '';
    return {
      isValid: stringValue.trim() !== '',
      message: 'Bitte ausfüllen.'
    };
  },
  email: value => ({
    isValid: /\S+@\S+\.\S+/.test(value),
    message: 'Bitte geben Sie eine gültige E-Mail-Adresse an.'
  }),
  minLength: length => value => ({
    isValid: value.length >= length,
    message: `Mindestens ${length} Zeichen benötigt.`
  }),
  maxLength: length => value => ({
    isValid: value.length <= length,
    message: `Höchstens ${length} Zeichen erlaubt.`
  }),
  number: value => ({
    isValid: (value == 'null') || !isNaN(parseNumber(value)),
    message: `Bitte geben Sie eine gültige Zahl ein.`
  }),
  plz: value => ({
    isValid: (!isNaN(value) && value.length === 4),
    message: 'Bitte geben Sie eine gültige Postleitzahl ein. (4 Ziffern)'
  }),
  iban: value => ({
    isValid: /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(value),
    message: 'Bitte geben Sie einen gültigen IBAN ein.'
  }),
  phoneNumber: value => ({
    isValid: /^(\+43|06)\d{8,10}$/.test(value),
    message: 'Bitte geben Sie eine gültige Telefonnummer an.'
  }),
  date: value => ({
    isValid: value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value),
    message: 'Bitte geben Sie ein gültiges Datum an (TT.MM.JJJJ).'
  }),
};

const CustomInput = forwardRef(({ rules, label, style, children, formData, sendChat, ...props }, ref) => {
  const [value, setValue] = useState((formData && formData[props.name] !== undefined) ? formData[props.name] : "");
  const [errors, setErrors] = useState([]);
  const [validationContextError, setValidationContextError] = useState([]);
  const { registerValidator } = useValidation();

  useEffect(() => {
    if (formData && props.name in formData) {
      setValue(formData[props.name]);
    }
  }, [formData, props.name]);

  // Assign validateField method to ref so that it can be accessed in parent
  useImperativeHandle(ref, () => ({
    validateField
  }));

  // Check if this field is numeric (e.g. number or plz)
  const isNumberField = rules.some(r => r.ruleKey === 'number');

  // Formatting functions
  const formatNumber = (val) => {
    if (val === '' || val == null || isNaN(val)) return val;
    return new Intl.NumberFormat('de-DE').format(Number(val));
  };

  const validateField = (new_value = value) => {
    //console.log("------\nvalidateField in CustomInput for label: ", label);
    //console.log('new_value: ', new_value);
    //console.log('value: ', value);

    setErrors([]); // Reset errors

    const newErrors = rules.map(rule => {
      const { ruleKey, ruleParam, customRule } = rule;
      let validationRule = customRule;
      if (!validationRule && ruleKey) {
        validationRule = validationRules[ruleKey];
        if (typeof ruleParam !== 'undefined') {
          validationRule = validationRule(ruleParam);
        }
      }

      if (validationRule) {
            //console.log("VALUE BEFORE CHECK: " + new_value);
        const validationResponse = validationRule(String(new_value));
        if (!validationResponse.isValid) {
          return validationResponse.message;
        }
      }
      return null;
    }).filter(error => error !== null);

    setErrors(newErrors);
    //console.log('newErrors: ', newErrors);
    //console.log("------\n");
    return newErrors.length === 0;
  };

  useEffect(() => {
    if (!rules || rules.length === 0) {
      throw new Error("CustomInput requires 'rules' prop with at least one rule.");
    }
  }, [rules, value]);

  useEffect(() => {
    //console.log('validator for field: ', props.name);
    registerValidator(validateField);
  }, []);

useEffect(() => {
  //console.log(`[useEffect] value changed:`, value);
}, [value]);


  const handleChange = (event) => {
    //console.log('event.target.value: ', event.target.value);
    let newValue = event.target.value;

    if (isNumberField) {
      const raw = parseNumber(newValue);
      if (raw === '' || isNaN(raw)) {
        setValue(newValue);
        return;
      }
      setValue(Number(raw));
    } else {
      setValue(newValue);
    }
  };

  // Trigger validateField when value changes
  useEffect(() => {
    if (value !== '') {
      validateField();
    }
  }, [value]);

  return (
    <>
      <div className="inputField">
        <div className="form-floating">
          <input
            {...props}
            ref={ref}
            style={style}
            value={isNumberField ? formatNumber(value) : value}
            className="form-control"
            placeholder={label}
            inputMode={isNumberField ? "numeric" : undefined}
            onChange={handleChange}
          />
          <label htmlFor={props.id}>{label}</label>
          {errors.map((error, index) => (
                  <div key={index} className="error-message" >{error}</div>
          ))}
        </div>
      </div>
      <div className='info-wrapper'>
        {formData?.info?.[props.name] && (
          <InfoIcon
            onClick={() => {
                //console.log("Mouse entered");
              if (formData && formData['info'] && formData['info'][props.name]) {
                    sendChat(formData['info'][props.name]); // Call sendChat with the correct data
              }

                // Scroll to the element with ID #info-icon-container
              const element = document.getElementById('hilfe');
              if (element) {
                    element.scrollIntoView({ behavior: 'smooth' }); // Smooth scroll to the element
              }
            }}
          >
            {formData && (formData['info'] && formData['info'][props.name]) || ""}
          </InfoIcon>
        )}
      </div>
    </>
  );
});

export {CustomInput, validationRules};
